import { useMutation } from '@tanstack/react-query';
import { api } from '@/shared/axios-client';
import type { ScmProviderId } from '@/types/scm-provider';
import type { ReleasePipelineSelection } from './getScriptCandidates';
import { withScmProvider, scmAuthHeaders } from './scmClient';
import type { PipelinePlatform } from '@/types/pipeline-platform';

export interface backendPipeline {
  releasePipelineId: number;
  releasePipelineName: string;
  lastReleaseId: null;
  lastReleaseProcessedAt: null;
}

export interface JenkinsDetectConfig {
  baseUrl: string;
  username: string;
  apiToken: string;
  jobName: string;
  jobUrl?: string;
}

export interface FileItem {
  path: string;
  size: number;
  compressedSize: number;
  isDirectory: boolean;
  content: string;
}

export interface RepoResponse {
  files: FileItem[];
  fileCount: number;
}

export interface DetectRepositoryBody {
  organization: {
    accountId: string;
    accountName: string;
    accountUri: string;
  };
  allProjects: {
    id: string;
    name: string;
    repositories: unknown[];
  }[];
  selectedProjectId: string;
  selectedRepositoryId: string;
  selectedBranch: { name: string; isDefault: boolean };
  repoUrl: string;
  onboardedBy: string;
  pipelineId: string | number;
  pipelineName: string;
  commitId: string;
  testAutomation: {
    location: string;
    testOrgName: string;
    testProjectName: string;
    testAdoRepoId: string;
    testRepoUrl?: string;
    testBranchName?: string;
    testScriptsRootPath?: string;
    testScriptsRootPaths?: string[];
    selectedScriptPaths?: string[];
    releaseSelections?: ReleasePipelineSelection[];
    testPipelineId: string;
    testPipelineName: string;
    testPat: string;
    /** Test CI platform selected after the test branch. */
    pipelinePlatform?: PipelinePlatform;
    jenkins?: JenkinsDetectConfig;
  };
  frontendUrl: string;
  backendUrl: string;
  pipelineStageNames: string[];
  releaseConfig: {
    organization: string;
    project: string;
    pat: string;
    pipelines: backendPipeline[];
  };
}

export interface TestScriptIndexResult {
  inventoryId?: string;
  status?: 'processing' | 'completed' | 'failed' | 'skipped';
  selectedCount?: number;
  inserted?: number;
  failed?: number;
  message?: string;
  buildId?: number;
  commitId?: string;
}


export interface DetectRepositoryResponse {
  success?: boolean;
  message?: string;
  onboardData: {
    activeRepositoryId: string;
    activeBranchId: string;
  };
  commitId: string;
  repoFiles: {
    data: {
      repoResponse: RepoResponse;
    };
  };
  testScriptIndex?: TestScriptIndexResult | null;
}

export const detectRepository = (
  provider: ScmProviderId,
  body: DetectRepositoryBody,
  patToken: string,
): Promise<DetectRepositoryResponse> => {
  return api.post(
    '/api/scm/detect',
    withScmProvider(provider, body),
    scmAuthHeaders(patToken),
  );
};

export const useDetectRepository = (
  provider: ScmProviderId,
  patToken: string,
) => {
  return useMutation({
    mutationFn: (body: DetectRepositoryBody) =>
      detectRepository(provider, body, patToken),
  });
};
